import { useIonViewWillEnter } from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type PaginatedReviewsResponseDto,
  Permission,
  ReputationApi,
  ReviewsApi,
  type ReviewWithReplyDto,
} from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useReputationScopeLocation,
  useReputationSync,
} from '@/core/components/reputation/reputation-sync-context';
import { createBulkReplyDraftsForReviews } from '@/core/components/reputation/reviews/reviews-bulk-drafts';
import {
  countActiveChipFilters,
  createDefaultReviewsFilters,
  type ReviewsFilterValues,
  type ReviewsInboxTab,
} from '@/core/components/reputation/reviews/reviews-filters';
import {
  clearInboxFocusFromNavigation,
  drilldownStateFromSearch,
  getInboxFocus,
} from '@/core/components/reputation/reviews/reviews-inbox-url';
import {
  buildReviewsListQuery,
  LOCATIONS_LIMIT,
  REVIEWS_PAGE_SIZE,
} from '@/core/components/reputation/reviews/reviews-list-query';
import { useDebounce } from '@/core/hooks/use-debounce';
import { useLocale } from '@/core/hooks/use-locale';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import {
  useReputationDrivers,
  useReputationOverview,
} from '@/core/hooks/use-reputation';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { reviewsQueryKeys } from '@/core/query-keys/reputation-query-keys';
import { statusBoardQueryKeys } from '@/core/query-keys/status-board-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

/** Server cap for one bulk-draft request (see BulkCreateReplyDraftsDto). */
const BULK_DRAFT_LIMIT = 25;

/** A review is urgent when it is unanswered and low-rated or negative. */
export function isUrgentReview(review: ReviewWithReplyDto): boolean {
  return (
    !review.reply?.text &&
    (review.rating <= 2 || review.sentiment?.polarity === 'negative')
  );
}

/** True while no reply is published or drafted — eligible for bulk AI drafts. */
function canDraft(review: ReviewWithReplyDto): boolean {
  if (review.reply?.status === 'published' && review.reply.text?.trim()) {
    return false;
  }
  return !review.replyDraft;
}

export function useReviewsInbox() {
  const dict = useDictionary();
  const queryClient = useQueryClient();
  const workspace = useCurrentWorkspace();
  const reviewsDict = dict.reviews;
  const repDict = dict.reputation;
  const routerLocation = useLocation();
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canReply = can(Permission.REPUTATION_REPLY);
  const locale = useLocale();
  const workspaceId = workspace?.id ?? '';

  const inboxFocus = useMemo(
    () => getInboxFocus(routerLocation),
    [routerLocation],
  );
  const focusReviewId = inboxFocus.reviewId;
  const focusHandledRef = useRef<string | null>(null);

  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery.trim(), 300);
  const [filters, setFilters] = useState<ReviewsFilterValues>(() =>
    createDefaultReviewsFilters(
      drilldownStateFromSearch(
        routerLocation.search,
        getInboxFocus(routerLocation).reviewId,
      ),
    ),
  );
  const [page, setPage] = useState(1);

  const applyDrilldownFromSearch = useCallback(
    (search: string, focusId?: string) => {
      setFilters((prev) => ({
        ...prev,
        ...drilldownStateFromSearch(search, focusId),
      }));
      setPage(1);
    },
    [],
  );

  // Ionic keeps this page alive in the tab stack, so URL-only navigations
  // (dashboard insights, analytics drilldowns) must re-seed the filters.
  useEffect(() => {
    const search = routerLocation.search;
    const focusId = getInboxFocus(routerLocation).reviewId;
    queueMicrotask(() => applyDrilldownFromSearch(search, focusId));
  }, [
    routerLocation.search,
    routerLocation.state,
    routerLocation,
    applyDrilldownFromSearch,
  ]);

  useIonViewWillEnter(() => {
    applyDrilldownFromSearch(
      routerLocation.search,
      getInboxFocus(routerLocation).reviewId,
    );
    if (!workspaceId) return;
    void ReputationApi.markReviewsSeen({
      path: { workspaceId },
      body: {},
    })
      .then(() => {
        void queryClient.invalidateQueries({
          queryKey: statusBoardQueryKeys.root,
        });
        void queryClient.invalidateQueries({
          queryKey: reviewsQueryKeys.all(workspaceId),
        });
      })
      .catch(() => {
        // Best-effort; badge clears on next successful engage.
      });
  });

  const { locations } = useLocations(workspaceId || undefined, {
    limit: LOCATIONS_LIMIT,
  });

  const scopedLocationId =
    filters.locationIds.length === 1 ? filters.locationIds[0] : undefined;
  useReputationScopeLocation(scopedLocationId);
  const { isSyncCompleteForScope } = useReputationSync();

  const { data: drivers } = useReputationDrivers(workspaceId || undefined, {
    locationId: scopedLocationId,
  });
  // Header stats + tab counts. Meaningful for the workspace or one location;
  // a multi-location filter has no matching aggregate.
  const statsScoped = filters.locationIds.length <= 1;
  const { data: overview } = useReputationOverview(workspaceId || undefined, {
    locationId: scopedLocationId,
  });

  const aspectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const driver of [
      ...(drivers?.positive ?? []),
      ...(drivers?.negative ?? []),
    ]) {
      map.set(driver.key, driver.label);
    }
    for (const key of filters.aspects) {
      if (!map.has(key)) map.set(key, key);
    }
    return [...map.entries()]
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [drivers, filters.aspects, locale]);

  const { data: reviewsResponse, isLoading: reviewsLoading } = useQuery({
    queryKey: reviewsQueryKeys.list(
      workspaceId,
      filters.locationIds,
      filters.sentiments,
      filters.aspects,
      filters.tab,
      filters.ratings,
      debouncedSearch,
      page,
      REVIEWS_PAGE_SIZE,
      page === 1 ? focusReviewId : undefined,
    ),
    queryFn: async () => {
      const res = await ReputationApi.listWorkspaceReviews({
        path: { workspaceId },
        query: buildReviewsListQuery(
          filters,
          debouncedSearch,
          page,
          REVIEWS_PAGE_SIZE,
          page === 1 ? focusReviewId : undefined,
        ),
      });
      return res.data as PaginatedReviewsResponseDto | undefined;
    },
    enabled: !!workspaceId,
    staleTime: 60 * 1000,
  });

  const reviews: ReviewWithReplyDto[] = useMemo(
    () => reviewsResponse?.reviews ?? [],
    [reviewsResponse],
  );
  const total = reviewsResponse?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / REVIEWS_PAGE_SIZE));
  const hasActiveFilters =
    countActiveChipFilters(filters) > 0 || !!debouncedSearch;

  const setTab = (tab: ReviewsInboxTab) => {
    setFilters((prev) => ({ ...prev, tab }));
    setPage(1);
  };
  const onFiltersChange = (next: ReviewsFilterValues) => {
    setFilters(next);
    setPage(1);
  };
  const clearFilters = () => {
    setFilters((prev) => createDefaultReviewsFilters({ tab: prev.tab }));
    setSearchQuery('');
    setPage(1);
  };
  const onSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of locations) map.set(loc.id, loc.name ?? loc.id);
    return map;
  }, [locations]);

  const invalidateReviews = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: reviewsQueryKeys.all(workspaceId),
      }),
    [queryClient, workspaceId],
  );

  const invalidateStats = useCallback(async () => {
    await Promise.all([
      invalidateReviews(),
      queryClient.invalidateQueries({ queryKey: ['reputation', 'overview'] }),
      queryClient.invalidateQueries({ queryKey: statusBoardQueryKeys.root }),
    ]);
  }, [invalidateReviews, queryClient]);

  // Selection: an explicit pick wins; otherwise the first row is active so the
  // split view is never empty.
  const activeReview = useMemo(
    () =>
      reviews.find((review) => review.id === activeReviewId) ??
      reviews[0] ??
      null,
    [reviews, activeReviewId],
  );

  const selectRelative = useCallback(
    (delta: 1 | -1) => {
      if (reviews.length === 0) return;
      const index = reviews.findIndex((r) => r.id === activeReview?.id);
      const next =
        reviews[Math.min(reviews.length - 1, Math.max(0, index + delta))];
      if (next) setActiveReviewId(next.id);
    },
    [reviews, activeReview],
  );

  // Deep link (?reviewId=) → select the review, then drop the param.
  useEffect(() => {
    if (!focusReviewId) {
      focusHandledRef.current = null;
      return;
    }
    if (reviewsLoading || focusHandledRef.current === focusReviewId) return;

    const target = reviews.find((item) => item.id === focusReviewId);
    if (!target && !isSyncCompleteForScope) return;
    focusHandledRef.current = focusReviewId;

    if (!target) {
      toast.error(reviewsDict.list.focusNotFound);
    } else {
      queueMicrotask(() => setActiveReviewId(target.id));
    }
    const cleared = clearInboxFocusFromNavigation(
      routerLocation.pathname,
      routerLocation.search,
    );
    navigateShell(`${cleared.pathname}${cleared.search}`, {
      intent: 'updateQuery',
      state: undefined,
    });
  }, [
    focusReviewId,
    reviews,
    reviewsLoading,
    isSyncCompleteForScope,
    reviewsDict.list.focusNotFound,
    routerLocation.pathname,
    routerLocation.search,
    navigateShell,
  ]);

  const suggestMutation = useMutation({
    mutationFn: async (review: ReviewWithReplyDto) => {
      const res = await ReviewsApi.suggestReply({
        path: { workspaceId, locationId: review.locationId },
        body: { reviewId: review.id },
      });
      return res.data?.suggestedText ?? '';
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(
        err?.response?.data?.error === 'openAiNotConfigured'
          ? reviewsDict.draft.notConfigured
          : reviewsDict.draft.failed,
      );
    },
  });

  const publishMutation = useMutation({
    mutationFn: async ({
      review,
      text,
    }: {
      review: ReviewWithReplyDto;
      text: string;
    }) => {
      const path = { workspaceId, locationId: review.locationId };
      const created = await ReviewsApi.createReplyDraft({
        path: { ...path, reviewId: review.id },
        body: { text, createdByAI: false },
      });
      const draftId = (created.data as { id: string }).id;
      await ReviewsApi.publishReplyDraft({
        path: { ...path, reviewId: review.id, draftId },
      });
    },
    onSuccess: async (_data, { review }) => {
      toast.success(reviewsDict.reply.statusPublished);
      // Answered reviews leave the open tab — advance to the neighbour.
      if (filters.tab === 'open') {
        const index = reviews.findIndex((r) => r.id === review.id);
        const next = reviews[index + 1] ?? reviews[index - 1];
        setActiveReviewId(next?.id ?? null);
      }
      await invalidateStats();
    },
    onError: () => toast.error(reviewsDict.reply.publishFailed),
  });

  const deleteMutation = useMutation({
    mutationFn: async (review: ReviewWithReplyDto) => {
      await ReviewsApi.deleteReply({
        path: {
          workspaceId,
          locationId: review.locationId,
          reviewId: review.id,
        },
      });
    },
    onSuccess: async () => {
      toast.success(reviewsDict.reply.deleteSuccess);
      await invalidateStats();
    },
    onError: () => toast.error(reviewsDict.reply.deleteFailed),
  });

  const draftAllMutation = useMutation({
    mutationFn: async (reviewIds: string[]) =>
      createBulkReplyDraftsForReviews({ workspaceId, reviewIds, reviews }),
    onSuccess: async (results) => {
      const success = results.filter((r) => r.success).length;
      const failed = results.length - success;
      await invalidateReviews();
      toast.success(
        failed === 0
          ? reviewsDict.bulk.success.replace('{count}', String(success))
          : reviewsDict.bulk.partialSuccess
              .replace('{success}', String(success))
              .replace('{failed}', String(failed)),
      );
    },
    onError: () => toast.error(reviewsDict.bulk.error),
  });

  const draftableIds = useMemo(
    () =>
      reviews
        .filter(canDraft)
        .slice(0, BULK_DRAFT_LIMIT)
        .map((review) => review.id),
    [reviews],
  );

  const unanswered = overview
    ? Math.max(0, overview.totalReviews - overview.respondedCount)
    : null;

  return {
    reviewsDict,
    repDict,
    locale,
    /**
     * `reputation:reply` — drafting, publishing, editing and deleting replies
     * all require it server-side; `reputation:read` alone is view-only.
     */
    canReply,
    filters,
    onFiltersChange,
    setTab,
    clearFilters,
    hasActiveFilters,
    locations,
    locationNameById,
    aspectOptions,
    searchQuery,
    onSearchChange,
    isSyncCompleteForScope,
    reviewsLoading,
    reviews,
    total,
    page,
    setPage,
    totalPages,
    activeReview,
    setActiveReviewId,
    selectRelative,
    stats: {
      scoped: statsScoped && !!overview,
      total: overview?.totalReviews ?? 0,
      answered: overview?.respondedCount ?? 0,
      unanswered: unanswered ?? 0,
      critical: overview?.unrepliedCriticalCount ?? 0,
      averageResponseHours: overview?.averageResponseHours ?? null,
    },
    draftAll: () => draftAllMutation.mutate(draftableIds),
    draftAllPending: draftAllMutation.isPending,
    draftAllCount: draftableIds.length,
    suggest: (review: ReviewWithReplyDto) =>
      suggestMutation.mutateAsync(review),
    isSuggesting: (reviewId: string) =>
      suggestMutation.isPending && suggestMutation.variables?.id === reviewId,
    publish: (review: ReviewWithReplyDto, text: string) =>
      publishMutation.mutateAsync({ review, text }).catch(() => undefined),
    isPublishing: (reviewId: string) =>
      publishMutation.isPending &&
      publishMutation.variables?.review.id === reviewId,
    removeReply: (review: ReviewWithReplyDto) => deleteMutation.mutate(review),
    isDeleting: (reviewId: string) =>
      deleteMutation.isPending && deleteMutation.variables?.id === reviewId,
  };
}

export type ReviewsInbox = ReturnType<typeof useReviewsInbox>;
